import type { TeamChannelDto, TeamMessageDto } from "@bafe/api-client";
import { teamLastMessageCopy } from "@bafe/types";
import type { QueryClient } from "@tanstack/react-query";

import { queryKeys } from "./query.js";

export function isTeamLiveMessage(value: unknown): value is {
	type: "message";
	channelId: string;
	message: TeamMessageDto;
} {
	if (!value || typeof value !== "object") return false;
	const event = value as { type?: unknown; channelId?: unknown; message?: unknown };
	if (event.type !== "message" || typeof event.channelId !== "string" || !event.message || typeof event.message !== "object") {
		return false;
	}
	const message = event.message as { id?: unknown };
	return typeof message.id === "string";
}

/** Write a live Team event into the cache so the other person sees it at once. */
export function applyTeamLiveMessage(queryClient: QueryClient, channelId: string, message: TeamMessageDto): void {
	queryClient.setQueryData<TeamMessageDto[]>(queryKeys.teamMessages(channelId), (prev) => {
		if (!prev) return [message];
		const index = prev.findIndex((item) => item.id === message.id);
		const next =
			index === -1 ? [...prev, message] : prev.map((item) => (item.id === message.id ? message : item));
		return next.toSorted((left, right) => Date.parse(left.createdAt) - Date.parse(right.createdAt));
	});
	queryClient.setQueryData<TeamChannelDto[]>(queryKeys.teamChannels, (prev) => {
		if (!prev) return prev;
		const thread = queryClient.getQueryData<TeamMessageDto[]>(queryKeys.teamMessages(channelId));
		const latest = [...(thread ?? [])].reverse().find((item) => !item.deletedAt);
		return prev.map((channel) =>
			channel.id === channelId
				? {
						...channel,
						lastMessage: latest
							? teamLastMessageCopy(latest.body, latest.mediaKind, latest.deletedAt)
							: null,
						lastMessageAt: latest
							? (latest.editedAt ?? latest.deletedAt ?? latest.createdAt)
							: channel.lastMessageAt,
					}
				: channel,
		);
	});
}
